import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Button, H3, LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CartPage.module.css';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  calculateCartSubtotal,
  cardRenderSizes,
  groupListingsByAuthor,
  isArrayLength,
} from '../../util/genericHelpers';
import CartPanel from './CartPanel';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { handleSubmit } from '../ListingPage/ListingPage.shared';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createSlug } from '../../util/urlHelpers';
import { extractListingDataForCart } from '../../util/cartHelpers';
import { currentPageResultIdsSelector } from './CartPage.duck';
import { currentUserSelector } from '../../ducks/user.duck';
import { userDisplayNameAsString } from '../../util/data';
import { useConfiguration } from '../../context/configurationContext';
import { createSellerCheckout } from '../../util/sellerCheckout';

const CartPage = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  // Redux state using useSelector
  const currentUser = useSelector(currentUserSelector);
  const currentPageResultIds = useSelector(currentPageResultIdsSelector);
  const listings = useSelector(state => getListingsById(state, currentPageResultIds));
  const sellerGroups = Object.values(groupListingsByAuthor(listings));
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const marketplaceCurrency = config.currency;
  // Local state for stock details
  const [stockDetails, setStockDetails] = useState([]);
  const [bookingSlot, setBookingSlot] = useState({});
  // Fetch and update stock details when listings change
  useEffect(() => {
    if (isArrayLength(listings)) {
      const listingDetails = listings.map(extractListingDataForCart);
      setStockDetails(listingDetails);
    } else {
      setStockDetails([]);
    }
  }, [listings.length]);

  // Extract listings once using useSelector
  const allListings = useSelector(state =>
    getMarketplaceEntities(state, currentPageResultIds.map(id => ({ id, type: 'listing' })))
  );

  // Safe helper function to find a listing by ID
  const getListing = id =>
    (isArrayLength(allListings) && allListings.find(l => l.id.uuid === id?.uuid)) || null;
  // Handle order submission
  const handleOrderSubmit = async group => {
    const sellerCheckout = createSellerCheckout(group, stockDetails);
    const listing = group[0];
    const listingParams = {
      id: listing.id.uuid,
      slug: createSlug(listing.attributes.title),
    };
    const cartItems = stockDetails.filter(item => item?.listingId === listing.id.uuid);
    await handleSubmit({
      params: listingParams,
      history,
      routes: routeConfiguration,
      currentUser,
      getListing,
      callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
        dispatch(setInitialValues(values, saveToSessionStorage)),
      onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
      cartItems,
      config,
    })({ quantity: cartItems[0]?.stockCount || 1, deliveryMethod: 'shipping', sellerCheckout });
  };

  const title = intl.formatMessage({ id: 'CartPage.title' });

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.cartPage}>
          <div className={css.fixWidthContainer}>
            <div className={css.headings}>
              <H3 as="h1" className={css.title}>
                {intl.formatMessage({ id: 'CartPage.heading' })}
              </H3>
              <NamedLink className={css.continuebtn} name="SearchPage">
                {intl.formatMessage({ id: 'CartPage.continueShopping' })}
              </NamedLink>
            </div>
            <p>{intl.formatMessage({ id: 'CartPage.separateOrders' })}</p>
            <div className={css.listingCards}>
              {isArrayLength(sellerGroups) ? (
                sellerGroups.map(group => (
                  <section
                    className={css.vendor}
                    key={group[0].author.id.uuid}
                    aria-labelledby={`seller-cart-${group[0].author.id.uuid}`}
                  >
                    <div className={css.venderCards}>
                      <h2 className={css.sellerName} id={`seller-cart-${group[0].author.id.uuid}`}>
                        {intl.formatMessage(
                          { id: 'CartPage.sellerCart' },
                          {
                            sellerName: userDisplayNameAsString(
                              group[0].author,
                              intl.formatMessage({ id: 'CartPage.seller' })
                            ),
                          }
                        )}
                      </h2>
                      {group.map(l => (
                        <CartPanel
                          className={css.listingCard}
                          key={l?.id?.uuid}
                          listing={l}
                          renderSizes={cardRenderSizes(false)}
                          currentUser={currentUser}
                          intl={intl}
                          stockDetails={stockDetails}
                          setStockDetails={setStockDetails}
                          currentCartItem={
                            isArrayLength(stockDetails)
                              ? {
                                  item: stockDetails.find(sd => sd.listingId === l?.id?.uuid),
                                  index: stockDetails.findIndex(sd => sd.listingId === l?.id?.uuid),
                                }
                              : { item: null, index: null }
                          }
                          marketplaceCurrency={marketplaceCurrency}
                          setBookingSlot={setBookingSlot}
                          bookingSlot={bookingSlot}
                        />
                      ))}
                    </div>
                    <div className={css.checkoutSection}>
                      <div className={css.subtotal}>
                        <p>{intl.formatMessage({ id: 'CartPage.subtotal' })}</p>
                        <h3>
                          {calculateCartSubtotal(
                            stockDetails.filter(sd => group.find(l => l.id.uuid === sd.listingId)),
                            intl,
                            marketplaceCurrency
                          )}
                        </h3>
                      </div>
                      <div className={css.totalItems}>
                        <p>
                          {intl.formatMessage(
                            { id: 'CartPage.totalItems' },
                            { count: group.length }
                          )}
                        </p>
                      </div>
                      <div className={css.checkoutButton}>
                        <Button
                          type="button"
                          aria-label={intl.formatMessage(
                            { id: 'CartPage.checkoutSeller' },
                            {
                              sellerName: userDisplayNameAsString(
                                group[0].author,
                                intl.formatMessage({ id: 'CartPage.seller' })
                              ),
                            }
                          )}
                          onClick={() => handleOrderSubmit(group)}
                        >
                          {intl.formatMessage({ id: 'CartPage.checkout' })}
                        </Button>
                      </div>
                    </div>
                  </section>
                ))
              ) : (
                <div>{intl.formatMessage({ id: 'CartPage.emptyCart' })}</div>
              )}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CartPage;
